import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiResendLoginOtp } from '../services/api';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import OtpInput from '../components/OtpInput';
import { routeForRole } from '../utils/roleRoute';
import { validateEmail } from '../utils/signupValidation';
import { clearEmailCodeLink, codeRequestedHere, readEmailCodeLink } from '../utils/emailCodeLink';
import { APP_VERSION, CONTACT_EMAIL } from '../constants/site';
import './auth.css';

// Doit rester ≥ au délai de garde serveur : proposer un renvoi que le serveur
// refuserait donnerait l'impression d'un bouton mort.
const RESEND_COOLDOWN_SECONDS = 60;
const OTP_LENGTH = 6;

const Login = () => {
  const navigate = useNavigate();
  const { requestLoginOtp, verifyOtpAndLogin } = useAuth();
  // Bouton « Me connecter » de l'e-mail : l'adresse et le code arrivent déjà saisis.
  const [link] = useState(() => {
    const l = readEmailCodeLink();
    return l.mail && l.code ? l : null;
  });
  const [step, setStep] = useState(link ? 'otp' : 'credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState(link?.mail || '');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState(link?.code || '');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Refus qui n'est pas une erreur de saisie : identifiants corrects, mais le
  // compte attend l'autorisation du représentant légal. Présenté à part pour ne
  // pas afficher un message rouge d'échec sur quelque chose que l'utilisatrice
  // ne peut pas corriger elle-même.
  const [guardianPending, setGuardianPending] = useState('');
  // Renvoi du code. Le décompte démarre dès l'arrivée sur l'écran : le premier
  // code vient d'être envoyé, il n'y a rien à renvoyer dans la seconde qui suit.
  const [resendIn, setResendIn] = useState(0);
  const [resendNotice, setResendNotice] = useState('');
  // StrictMode rejoue les effets : sans ce verrou, le code partirait deux fois.
  const autoSubmitted = useRef(false);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  const handleResend = async () => {
    setError('');
    setResendNotice('');
    setResendIn(RESEND_COOLDOWN_SECONDS);
    try {
      await apiResendLoginOtp(email.trim());
      // Le serveur répond 202 même pour une adresse inconnue : le message reste
      // donc au conditionnel, il n'y a rien à confirmer sans en dire trop.
      setResendNotice('Si une connexion est en cours, un nouveau code vient de partir.');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCredentialsSubmit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    setError('');
    setGuardianPending('');
    const emailError = validateEmail(email);
    if (emailError) { setError(emailError); return; }
    if (!password) { setError('Le mot de passe est requis.'); return; }
    setSubmitting(true);
    try {
      const res = await requestLoginOtp(email.trim(), password);
      // Appareil de confiance : login terminé sans OTP, on redirige directement.
      if (res?.loggedIn) {
        navigate(routeForRole(res.profile?.role));
        return;
      }
      setStep('otp');
      setCode('');
      setResendNotice('');
      setResendIn(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      // Compte créé mais jamais activé : plutôt que d'afficher une impasse, on
      // envoie sur l'écran de saisie du code, qui permet aussi d'en redemander un.
      if (err.code === 'email_not_verified') {
        navigate('/verification', { state: { email: email.trim() } });
        return;
      }
      if (err.code === 'guardian_consent_required') {
        setGuardianPending(err.message);
        return;
      }
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // `typedCode` : passé par OtpInput.onComplete, l'état `code` n'étant pas encore à jour.
  const handleOtpSubmit = async (e, typedCode = code) => {
    e?.preventDefault();
    if (submitting) return;
    setError('');
    if (typedCode.length !== OTP_LENGTH) {
      setError(`Saisissez les ${OTP_LENGTH} chiffres du code.`);
      return;
    }
    setSubmitting(true);
    try {
      const profile = await verifyOtpAndLogin(email.trim(), typedCode);
      navigate(routeForRole(profile?.role));
    } catch (err) {
      setError(err.message);
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvert depuis l'e-mail : on efface le fragment, et on valide d'office si c'est
  // ce navigateur qui a demandé le code (sinon il attend un appui sur « Valider »).
  useEffect(() => {
    clearEmailCodeLink();
    if (link && codeRequestedHere() && !autoSubmitted.current) {
      autoSubmitted.current = true;
      handleOtpSubmit(null, link.code);
    }
    // Une seule fois, à l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>

        {step === 'credentials' ? (
          <form onSubmit={handleCredentialsSubmit} noValidate>
            <p className="subtitle">Connectez-vous</p>

            <FormInput
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="votre.email@exemple.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <FormInput
              label="Mot de passe"
              type="password"
              name="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {error && <p className="auth-error">{error}</p>}

            {guardianPending && (
              <div className="auth-notice">
                <p className="auth-notice__title">Autorisation parentale en attente</p>
                <p className="auth-notice__text">{guardianPending}</p>
                <p className="auth-notice__text">
                  Une question ? Écris-nous à{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
              </div>
            )}

            <div className="button-group">
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? 'Connexion…' : 'Connexion'}
              </button>
            </div>

            <div className="auth-link-row">
              <button type="button" className="auth-link-btn" onClick={() => navigate('/mot-de-passe-oublie')}>
                Mot de passe oublié ?
              </button>
            </div>

            <div className="auth-link-row">
              <span>Pas encore inscrit ? </span>
              <button type="button" className="auth-link-btn" onClick={() => navigate('/inscription')}>
                Créer un compte
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleOtpSubmit} noValidate>
            <p className="subtitle">Un code à 6 chiffres a été envoyé à {email}</p>

            <OtpInput
              label="Code de connexion"
              idPrefix="login-otp"
              value={code}
              onChange={(v) => { setCode(v); setError(''); }}
              onComplete={(v) => handleOtpSubmit(null, v)}
              disabled={submitting}
              error={!!error}
            />

            {error && <p className="auth-error">{error}</p>}
            {link && !error && !submitting && code === link.code && (
              <p className="auth-hint">Code repris de l'e-mail : touchez « Valider » pour vous connecter.</p>
            )}
            {resendNotice && <p className="auth-hint">{resendNotice}</p>}

            <div className="button-group">
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {submitting ? 'Vérification…' : 'Valider'}
              </button>
            </div>

            <div className="auth-link-row auth-link-row--split">
              <button type="button" className="auth-link-btn" onClick={() => { setStep('credentials'); setError(''); }}>
                ‹ Retour
              </button>
              {/* Sans ce bouton, une utilisatrice qui ne reçoit pas son code doit
                  ressaisir adresse et mot de passe pour en redemander un. */}
              <button
                type="button"
                className="auth-link-btn"
                onClick={handleResend}
                disabled={resendIn > 0}
              >
                {resendIn > 0 ? `Renvoyer le code (${resendIn} s)` : 'Renvoyer le code'}
              </button>
            </div>
          </form>
        )}

        <p className="auth-version">Version {APP_VERSION}</p>
      </div>
    </div>
  );
};

export default Login;
